import React from 'react';
import { ArcBatteryLife, ArcButton, ArcIconButton, ArcOutlinedInput, ArcProgressBar, ArcStack, ArcText, ArcBox } from "@src/core/ui";
import { SelectedDroneProps } from './types';
import { ReactComponent as SignalIcon } from '@src/assets/icons/signal.svg';
import { ReactComponent as GpsIcon } from '@src/assets/icons/gps.svg';
import { InputAdornment } from '@mui/material';
import { EMPTY_STRING } from '@src/core/config';
import { arcConvertBetweenUnitTypes, arcSpatiumUnit, arcToFixed, toSi } from '@src/core/extensions';
import { MeasurementType } from '@src/core/features/settings/entities';
import { SettingUnitType, useGetSettingByUnitType } from '@src/core/features';
import { useFlightProvider } from '@src/features/order/provider';
import { toast } from 'react-toastify';
import { ArcSocketContext, ChannelRequestEnum } from '@src/core/socket';
import { useDispatch } from 'react-redux';
import { addOptimisticMissionSerial, setShowTakeoffConfirm, useTakeOffMutation } from '@src/features/drone/redux';
import ConfirmTakeoff from '@src/features/drone/components/confirm-takeoff';
import { ReactComponent as MenuIcon } from '@src/assets/icons/menu.svg';
import { RTKSettingsPopup } from '@src/features/drone/components/rtk-settings-popup';
import { useSearchParams } from 'react-router-dom';
import { getUnitType, useUnitType } from '@src/core/features/settings/units';
import { UnitType, UnitSystemType } from '@src/core/enums';
import { trackError, toTrackedError } from '@src/core/sentry';
import { useGlobalCamera } from '@src/core/providers/global-camera-provider';
import GoHomeAction from '@src/features/order/components/go-home-action';
import WinchDownAction from '@src/features/order/components/winch-down-action';


export const SelectedDrone: React.FC <SelectedDroneProps>= (props) => {
  const { drone, isOnMission, isStrictMission, isLoading: isOrderLoading } = props;
  const { battery, gpsInfo, flightData } = useFlightProvider();
  const { activeSource } = useGlobalCamera();
  const resetGoHere = activeSource?.resetGoHere ?? (() => {});
  const dispatch = useDispatch();
  const [takeOff, { isLoading }] = useTakeOffMutation();
  const { arcSocket, readyState } = React.useContext(ArcSocketContext);
  const flightAltitude = useGetSettingByUnitType(SettingUnitType.FLIGHT_ALTITUDE);
  const unitType = useUnitType();
  // FLIGHT_ALTITUDE setting now arrives in SI (m). The takeoff input is labeled and
  // edited in the user's display unit and submitted with unitSystem (backend converts
  // display -> metric), so convert SI -> display for the default.
  const flightAltitudeDisplay = React.useMemo(() => {
    const num = Number(flightAltitude.value);
    if (!Number.isFinite(num)) return flightAltitude.value;
    return String(arcConvertBetweenUnitTypes(num, MeasurementType.DISTANCE, UnitType.SI, getUnitType()));
  }, [flightAltitude.value, unitType]);
  const [takeOffAltitude, setTakeOffAltitude] = React.useState(flightAltitudeDisplay);
  const [, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (flightAltitude.value != null) {
      setTakeOffAltitude(flightAltitudeDisplay);
    }
  }, [flightAltitudeDisplay, flightAltitude.value, flightAltitude.unitValue]);
  const [showRtkSettings, setShowRtkSettings] = React.useState(false);
  const [showRtkPopup, setShowRtkPopup] = React.useState(false);
  const menuContainerRef = React.useRef<HTMLDivElement>(null);
  
  const [missionProgress, setMissionProgress] = React.useState<number>(0);
  

  React.useEffect(() => {
    if (flightData?.serialNumber === drone.serialNumber && typeof flightData?.missionProgress === 'number') {
      const newProgress: number = flightData.missionProgress;
      setMissionProgress((prev) => {
        return prev !== newProgress ? newProgress : prev;
      });
    }
  }, [flightData, drone.serialNumber]);
  
  React.useEffect(() => {
    setMissionProgress(0);
  }, [drone.serialNumber]);

  // Listen for takeoff confirmation event
  React.useEffect(() => {
    const handleTakeoffConfirmed = async () => {
      if (!drone.id) {
        toast.error('drone  id is undefined');
        return;
      }

      try {
        // The altitude input is in the user's display unit; the wire is always SI, so
        // convert before send and pass unitSystem as a constant guard (UNITS Standard R2/R6).
        const altitude = toSi(takeOffAltitude || flightAltitudeDisplay, MeasurementType.DISTANCE);
        await takeOff({ droneId: drone.id.toString(), altitude, unitSystem: UnitSystemType.METRIC }).unwrap();
        dispatch(addOptimisticMissionSerial(drone.serialNumber));
        setSearchParams({ droneSerial: drone.serialNumber });
        toast.success('Take Off Successfully');
      } catch (error: unknown) {
        trackError(toTrackedError(error), { code: 'FE_0023', extra: { flow: 'takeOffSelectedDrone' } });
        toast.error('Failed to take off');
        console.error('Take off error:', error);
      }
    };

    window.addEventListener('takeoffConfirmed', handleTakeoffConfirmed);
    return () => {
      window.removeEventListener('takeoffConfirmed', handleTakeoffConfirmed);
    };
  }, [drone, takeOff, takeOffAltitude, flightAltitude]);

  React.useEffect(() => {
    const socket = arcSocket();
    if (socket === null || socket.readyState !== WebSocket.OPEN) return;
    
    const payload = { type: ChannelRequestEnum.GET_FLIGHT_DATA, data: { serialNumber: drone.serialNumber  } };
    socket.send(JSON.stringify(payload));
        
    return () => {
      const payload = { type: ChannelRequestEnum.STOP_FLIGHT_DATA };
      socket.send(JSON.stringify(payload));
      
    };
  }, [readyState, drone.serialNumber, arcSocket]);

  const onTakeOff = () => {
    dispatch(setShowTakeoffConfirm(true));
  };

  const handleMenuClick = () => {
    setShowRtkSettings(!showRtkSettings);
  };

  const handleRtkSettingsClick = () => {
    setShowRtkSettings(false);
    setShowRtkPopup(true);
  };

  const handleCloseRtkPopup = () => {
    setShowRtkPopup(false);
  };


  return (
    <ArcStack bgcolor={'background.paper'} borderRadius={"4px"} padding={"14px 14px 14px 14px"} position='relative'>
      <ConfirmTakeoff takeOffAltitude={arcToFixed(Number(takeOffAltitude || flightAltitudeDisplay), 1)} />
      <RTKSettingsPopup open={showRtkPopup} onClose={handleCloseRtkPopup} droneId={drone.id.toString()} />
      <ArcStack direction='row' justifyContent='space-between' alignItems={'flex-start'}>

        <ArcStack spacing={0.4}>
          <ArcText.h5 > {drone.name} </ArcText.h5>
          <ArcText.caption fontSize={12} whiteSpace='nowrap'>{drone.serialNumber}</ArcText.caption>
        </ArcStack>
       
        <ArcStack direction='row' alignItems='center' position='relative' ref={menuContainerRef}>
          <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'> {drone.batteryHealth}% </ArcText.caption>
          <ArcBatteryLife percent={drone.batteryHealth} />
          <ArcStack position='relative'>
            <ArcIconButton onClick={handleMenuClick}> 
              <MenuIcon /> 
            </ArcIconButton>
            {showRtkSettings && (
              <ArcBox
                onClick={handleRtkSettingsClick}
                sx={{
                  position: 'absolute',
                  top: '90%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  padding: '13px 14px',
                  cursor: 'pointer',
                  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                  zIndex: 1000,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'grey.100',
                  },
                }}
              >
                <ArcText fontSize={12} color='black' fontWeight={500}>
                  RTK NTRIP Settings
                </ArcText>
              </ArcBox>
            )}
          </ArcStack>
        </ArcStack>

      </ArcStack>  
      <ArcStack direction='row' alignItems='center' justifyContent='space-between' marginTop={2}>
        {/* hide signal strength section for now(staging) */}
        {/*  <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace='nowrap'>SIGNAL</ArcText.caption>
          <ArcStack direction='row' alignItems='center' spacing={1}>
            <SignalIcon/>
            <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'>---</ArcText.caption>
          </ArcStack>
        </ArcStack>
        */}
   
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace='nowrap'>GNSS</ArcText.caption>
          <ArcStack direction='row' alignItems='center' spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'>{gpsInfo?.num_satellites}</ArcText.caption>
            <GpsIcon/>
            <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'>{gpsInfo?.fix_type}</ArcText.caption>
          </ArcStack>
        </ArcStack>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace='nowrap'>VOLTAGE</ArcText.caption>
          <ArcStack direction='row' alignItems='center' spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'>{arcToFixed(battery?.voltage_v)} V</ArcText.caption>
          </ArcStack>
        </ArcStack>
        <ArcStack>
          <ArcText.caption fontSize={8} whiteSpace='nowrap'>BATTERY EST.</ArcText.caption>
          <ArcStack direction='row' alignItems='center' spacing={1}>
            <ArcText.caption fontSize={12} whiteSpace='nowrap' color='white'>{EMPTY_STRING}</ArcText.caption>
          </ArcStack>
        </ArcStack>
      </ArcStack>

      {!isOnMission && <ArcStack marginTop={4} direction='row' alignItems='center' justifyContent='space-between'>    
        <ArcOutlinedInput 
          label='Takeoff Altitude'
          autoComplete='off'
          type='number'
          sx={{width: '47.7%', height: 40}}
          onChange={(e) => setTakeOffAltitude(e.target.value)}
          value={takeOffAltitude === '' ? '' : arcToFixed(Number(takeOffAltitude || flightAltitudeDisplay), 1)}
          InputLabelProps={{ shrink: true }} 
          slotProps={{ input: { 
            endAdornment: (
              <InputAdornment position='end'> 
                <ArcText.h5>{arcSpatiumUnit()}</ArcText.h5> 
              </InputAdornment> 
            )
          }}}
        />
        <ArcButton.brand disabled={isOrderLoading} sx={{width: '47.7%', height: 40}} isLoading={isLoading} onClick={onTakeOff}>
            Take off
        </ArcButton.brand>
      </ArcStack> }

      {isStrictMission && (
        <ArcStack direction='row' spacing={1} marginTop={3}>
          <GoHomeAction
            serialNumber={drone.serialNumber}
            resetGoHere={resetGoHere}
            sx={{ flex: 1, width: 'auto', height: 40 }}
          />
          <WinchDownAction
            serialNumber={drone.serialNumber}
            sx={{ flex: 1, width: 'auto', height: 40 }}
          />
        </ArcStack>
      )}

      {isOnMission && drone?.currentOrderId && <ArcStack marginTop={3}>
        <ArcProgressBar.mission value={missionProgress} />
        <ArcStack marginTop={1} direction='row' alignItems='center' justifyContent='space-between'>
          <ArcText fontSize={14} whiteSpace='nowrap' color='white'>Mission completion</ArcText>
          <ArcText fontSize={14} whiteSpace='nowrap' color='white'>
            {parseFloat(arcToFixed(missionProgress, 1))}%
          </ArcText>
        </ArcStack>
      </ArcStack>}
    </ArcStack>
  );
};